import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  changePasswordRequest,
  fetchMe,
  hasStoredSession,
  loginRequest,
  logoutRequest,
  onSessionEnded,
  type SessionUser,
} from '../lib/api';

type Status = 'loading' | 'anonymous' | 'authenticated';

interface AuthState {
  status: Status;
  user: SessionUser | null;
  /** The account is on a temporary password and must choose its own before doing anything else. */
  mustChangePassword: boolean;
  /** Why the last session ended, shown once on the login screen. */
  notice: string | null;
  login: (email: string, password: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>(hasStoredSession() ? 'loading' : 'anonymous');
  const [user, setUser] = useState<SessionUser | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Resume a session left in storage by asking the server who it belongs to.
  useEffect(() => {
    if (status !== 'loading') return;
    let cancelled = false;
    fetchMe()
      .then((me) => {
        if (cancelled) return;
        setUser(me.user);
        setMustChangePassword(me.mustChangePassword);
        setStatus('authenticated');
      })
      .catch(() => {
        if (!cancelled) setStatus('anonymous');
      });
    return () => {
      cancelled = true;
    };
  }, [status]);

  // The api client tells us when a session can no longer be kept alive.
  useEffect(
    () =>
      onSessionEnded((reason) => {
        setUser(null);
        setMustChangePassword(false);
        setStatus('anonymous');
        if (reason === 'expired') setNotice('Your session expired. Please sign in again.');
      }),
    [],
  );

  const login = useCallback(async (email: string, password: string) => {
    const session = await loginRequest(email, password);
    setNotice(null);
    setUser(session.user);
    setMustChangePassword(session.mustChangePassword);
    setStatus('authenticated');
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const session = await changePasswordRequest(currentPassword, newPassword);
    setUser(session.user);
    setMustChangePassword(false);
  }, []);

  const logout = useCallback(async () => {
    await logoutRequest();
    // Drop everything held in memory too, so the next person to sign in on this
    // browser starts from nothing.
    setUser(null);
    setMustChangePassword(false);
    setNotice(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo(
    () => ({ status, user, mustChangePassword, notice, login, changePassword, logout }),
    [status, user, mustChangePassword, notice, login, changePassword, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** For pages that only render for a signed-in user. */
export function useUser(): SessionUser {
  const { user } = useAuth();
  if (!user) throw new Error('useUser called with no signed-in user');
  return user;
}
